import { useEffect, useState } from "react";
import { AudioLines, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  PaletteHint,
  ResultSkeleton,
  SearchPalette,
  SuggestionChips,
} from "@/components/search/SearchPalette";
import { MIN_SEARCH_LENGTH, useSearchTerm } from "@/components/search/use-search-term";
import { pronunciationSearchPath } from "@/components/search/search-mode";
import { ClipWatchView } from "@/components/vocabulary/ClipWatchView";

import { useGuardedNavigate } from "@/hooks/use-guarded-navigate";
import { useVideoCaptionSearch } from "@/hooks/use-video-caption-search";
import type { VideoCaptionHit } from "@/lib/video-types";
import { typeMetaClass } from "@/lib/typography";

export interface PronunciationSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Clips in the rail. The footer opens the full desk for the rest. */
const CLIP_RESULT_LIMIT = 8;

/**
 * Common words, in English like every other piece of learning content — a
 * suggestion that does not look like what the learner will actually say teaches
 * nothing.
 */
const EXAMPLE_QUERIES = ["happy", "coffee", "travel"];

/**
 * "How is this word actually said?" — the search behind the sidebar's second
 * row, and the watch modal from a session's vocabulary row, with a field on it.
 *
 * It is deliberately **not** a list of caption lines that sends you somewhere
 * else: a search for a pronunciation should end in a voice. The body is
 * `ClipWatchView` — the same player, transport and clip rail the session
 * sidebar opens on a word — so clicking a line plays that moment in place.
 */
export function PronunciationSearchDialog({
  open,
  onOpenChange,
}: PronunciationSearchDialogProps) {
  const { t } = useTranslation();
  const guardedNavigate = useGuardedNavigate();

  const { term, setTerm, trimmed, liveTerm, pending, canSearch, canSubmit, typingTooShort } =
    useSearchTerm(open);

  const { hits, total, isLoading, isError, isSearchable } = useVideoCaptionSearch(
    open && canSearch ? trimmed : "",
    "all",
    CLIP_RESULT_LIMIT,
  );

  const [activeClip, setActiveClip] = useState<VideoCaptionHit | null>(null);

  // A different word is a different answer; so is a reopened dialog.
  useEffect(() => {
    setActiveClip(null);
  }, [open, trimmed]);

  // Land on the first match instead of a rail the reader has to click into —
  // same behaviour as the session sidebar's word dialog.
  useEffect(() => {
    if (!open || activeClip || hits.length === 0) return;
    setActiveClip(hits[0]);
  }, [open, activeClip, hits]);

  const go = (path: string) => {
    onOpenChange(false);
    void guardedNavigate(path);
  };

  const submit = () => {
    if (!canSubmit) return;
    go(pronunciationSearchPath(liveTerm));
  };

  const footerLabel = (() => {
    if (!canSubmit) return t("nav.search.footerIdle", "Type to search");
    // While the debounce is in flight the count belongs to the previous query.
    if (pending) {
      return t("nav.search.searchPronunciation", {
        query: liveTerm,
        defaultValue: "Search videos for “{{query}}”",
      });
    }
    // `total` counts videos, not lines (the RPC keeps one best moment per
    // video), so the label says videos.
    return total > 0
      ? t("nav.search.hearInVideos", {
          count: total,
          defaultValue: "Hear it in {{count}} videos",
        })
      : t("nav.search.searchPronunciation", {
          query: trimmed,
          defaultValue: "Search videos for “{{query}}”",
        });
  })();

  return (
    <SearchPalette
      open={open}
      onOpenChange={onOpenChange}
      width="wide"
      icon={AudioLines}
      title={t("nav.search.pronunciationTitle", "Pronunciation")}
      description={t(
        "nav.search.pronunciationDescription",
        "Hear a word said by a real speaker in the video library.",
      )}
      placeholder={t("nav.search.pronunciationPlaceholder", "Find a word in real speech...")}
      term={term}
      onTermChange={setTerm}
      onSubmit={submit}
      footerLabel={footerLabel}
      footerDisabled={!canSubmit}
      onFooterClick={submit}
      /**
       * A flex column, not a scrolling block: `ClipWatchView` sizes its player
       * and clip rail with `sm:flex-1` / `min-h-0`, so the *rail* has to be the
       * thing that scrolls. In a block body those did nothing — the desk grew
       * to the rail's 867px, the whole modal scrolled, and the rail was cut off
       * behind the footer with no scrollbar of its own.
       */
      bodyClassName="flex flex-col overflow-y-auto p-3"
    >
      {typingTooShort ? (
        <PaletteHint
          icon={SearchX}
          title={t("nav.search.tooShort", {
            count: MIN_SEARCH_LENGTH,
            defaultValue: "Type at least {{count}} characters.",
          })}
        />
      ) : !liveTerm ? (
        <PaletteHint
          icon={AudioLines}
          title={t("nav.search.pronunciationHint", "Hear a word said in a real video.")}
          description={t(
            "nav.search.pronunciationHintSecondary",
            "Type a word, then play the exact moment someone says it — slow it down and shadow the line.",
          )}
        >
          <SuggestionChips
            label={t("nav.search.tryExamples", "Try")}
            words={EXAMPLE_QUERIES}
            onPick={setTerm}
          />
        </PaletteHint>
      ) : !canSearch ? (
        <ResultSkeleton />
      ) : (
        <ClipWatchView
          query={trimmed}
          hits={hits}
          isLoading={isLoading}
          isError={isError}
          isSearchable={isSearchable}
          activeClip={activeClip}
          onSelectClip={setActiveClip}
          railHeader={
            hits.length > 0 ? (
              <span className={typeMetaClass}>{t("nav.search.moments", "Moments")}</span>
            ) : undefined
          }
        />
      )}
    </SearchPalette>
  );
}

export default PronunciationSearchDialog;
